import { useCallback, useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { useAuthContext } from 'context/useAuthContext';
import { IconChevronDown, IconPendoInfo } from 'components/Icons';
import { prefixClass } from 'lib/utils';
import MobileActionModal, { MobileActionOption } from '../../../Mobile/MobileActionModal/MobileActionModal';
import AvatarMini from '../../Common/AvatarMini';
import { TipsCard, defaultTips } from '../../Common/TipsCard';
import './MobileHeader.scss';

const TIPS_STORAGE_KEY = 'ksq2_tips_shown_v1';

/**
 * MobileHeader - Top header for mobile KSQ2 layout
 *
 * Features:
 * - Location selector with dropdown trigger
 * - User avatar with action modal (sign out)
 * - Dark background styling
 *
 * @see RN equivalent: ks-mobile-app/src/components/layout/TabScreenLayout.tsx (header section)
 */

interface MobileHeaderProps {
  /** Optional callback when location selector is clicked */
  onLocationPress?: () => void;
}

export const MobileHeader = ({ onLocationPress }: MobileHeaderProps) => {
  const prefix = prefixClass('ksq2-mobile-header');
  const currentLocation = useLocationsStore(state => state.currentLocation);
  const { signOut, user } = useAuthContext();

  // Avatar action modal state
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  // Tips card state
  const [isTipsOpen, setIsTipsOpen] = useState(false);
  const [showTipsPulse, setShowTipsPulse] = useState(false);

  // Show tips on first visit
  useEffect(() => {
    const hasSeenTips = localStorage.getItem(TIPS_STORAGE_KEY);
    if (!hasSeenTips) {
      setIsTipsOpen(true);
    }
  }, []);

  // Get user name for avatar
  const userName = user?.displayName || user?.email || '';

  const handleLocationPress = useCallback(() => {
    if (onLocationPress) {
      onLocationPress();
    }
  }, [onLocationPress]);

  const handleToggleActionModal = useCallback(() => {
    setIsActionModalOpen(prev => !prev);
  }, []);

  const handleCloseActionModal = useCallback(() => {
    setIsActionModalOpen(false);
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOut();
    localStorage.removeItem('recentLocations');
    setIsActionModalOpen(false);
  }, [signOut]);

  const handleOpenTips = useCallback(() => {
    setIsTipsOpen(true);
  }, []);

  const handleCloseTips = useCallback(() => {
    setIsTipsOpen(false);
    localStorage.setItem(TIPS_STORAGE_KEY, 'true');

    // Pulse the tips button after modal fades out
    setTimeout(() => {
      setShowTipsPulse(true);
      // Clear pulse after animation completes
      setTimeout(() => setShowTipsPulse(false), 600);
    }, 300);
  }, []);

  // Modal options
  const modalOptions: MobileActionOption[] = [{ name: 'Sign Out', onClick: handleSignOut }];

  const locationName = currentLocation?.name || 'Select Location';

  return (
    <>
      <header className={prefix()}>
        <button className={prefix('__location')} onClick={handleLocationPress} type="button">
          <span className={prefix('__location-name')}>{locationName}</span>
          <IconChevronDown />
        </button>
        <div className={prefix('__actions')}>
          <button
            className={classNames(prefix('__tips-trigger'), {
              [prefix('__tips-trigger--pulse')]: showTipsPulse,
            })}
            onClick={handleOpenTips}
            aria-label="Show tips"
          >
            <IconPendoInfo />
          </button>
          <div ref={avatarRef} className={prefix('__avatar')} onClick={handleToggleActionModal}>
            <AvatarMini id={user?.uid || 'user'} type="ks" name={userName} />
          </div>
        </div>
      </header>
      <TipsCard tips={defaultTips} isOpen={isTipsOpen} onClose={handleCloseTips} />
      <MobileActionModal
        isOpen={isActionModalOpen}
        onClose={handleCloseActionModal}
        options={modalOptions}
        triggerRef={avatarRef}
        align="right"
      />
    </>
  );
};

export default MobileHeader;
